import type {
  CompanySearchResponse,
  EvaluateRequest,
  EvaluateResponse,
  EventRequest,
  PostcodeResponse,
  SubmissionRequest,
  SubmissionResponse,
} from '../domain/contract';
import type { PublicSettings } from '../domain/settings';

export type AppSettings = PublicSettings & { bookingOrigins: string[] };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(code);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { ...init, headers: { 'content-type': 'application/json', ...init?.headers } });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; fields?: Record<string, string> };
    throw new ApiError(response.status, body.error ?? 'request_failed', body.fields);
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const api = {
  settings: () => request<AppSettings>('/api/settings'),
  postcode: (postcode: string) => request<PostcodeResponse>('/api/postcode', { method: 'POST', body: JSON.stringify({ postcode }) }),
  companies: (q: string, signal?: AbortSignal) => request<CompanySearchResponse>(`/api/companies?q=${encodeURIComponent(q)}`, { signal }),
  evaluate: (body: EvaluateRequest) => request<EvaluateResponse>('/api/evaluate', { method: 'POST', body: JSON.stringify(body) }),
  submit: (body: SubmissionRequest) => request<SubmissionResponse>('/api/submissions', { method: 'POST', body: JSON.stringify(body) }),
  submission: (id: string) => request<SubmissionResponse>(`/api/submissions/${encodeURIComponent(id)}`),
  booking: (id: string, meetingAt: string | null) =>
    request<{ status: string }>(`/api/submissions/${encodeURIComponent(id)}/booking`, { method: 'POST', body: JSON.stringify({ meetingAt }), keepalive: true }),
  event: (body: EventRequest) => request<void>('/api/events', { method: 'POST', body: JSON.stringify(body), keepalive: true }),
};

/** Retries transient failures (network, 5xx, 429) with the same payload. */
export async function withRetry<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  let last: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (error) {
      last = error;
      const retryable = !(error instanceof ApiError) || error.status >= 500 || error.status === 429;
      if (!retryable || i === attempts - 1) throw error;
      await new Promise((resolve) => setTimeout(resolve, 600 * 2 ** i));
    }
  }
  throw last;
}
